import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useGlobalData } from "../context/GlobalDataContext";
import { getJobSelectionBySlug, getHFHeadItems, getMFRecruitments } from "../api/api";
import "./SelectionJobPage.css";
import "./SingleColumnTable.css";

const renderList = (items = []) => (
  <ul>
    {items.map((item, index) => (
      <li key={`${item}-${index}`}>{item}</li>
    ))}
  </ul>
);

const mapLegacyDataToPage = (jobSlug, jobs, admissions, heads, recruitments) => {
  const job = jobs.find((entry) => entry.slug === jobSlug);
  const admission = admissions.find((entry) => (entry.jobSlug || entry.slug) === jobSlug);
  const head = heads.find((entry) => entry.jobSlug === jobSlug || entry.slug === jobSlug);
  const recruitment = recruitments.find((entry) => entry.jobSlug === jobSlug || entry.slug === jobSlug);

  if (!head && !recruitment && !job) {
    return null;
  }

  return {
    jobTitle: job?.title || recruitment?.title || head?.postName || jobSlug,
    jobSlug,
    admissionSlug: admission?.slug || "",
    posts: [],
    tableData: head
      ? [
          {
            columnTitle: "Name of Post",
            columnItems: [head.postName]
          },
          {
            columnTitle: "Post Date / Update",
            columnItems: [head.postDate]
          }
        ]
      : [],
    tableData2: recruitment
      ? [
          {
            columnTitle: "Important Dates",
            columnItems: [recruitment.importantDates]
          },
          {
            columnTitle: "Application Fee",
            columnItems: [recruitment.applicationFee]
          },
          {
            columnTitle: "Age Limitation",
            columnItems: [recruitment.ageLimit]
          }
        ]
      : [],
    vacancyDetails: (recruitment?.posts || []).map((post) => ({
      post: post.postName || "",
      totalPost: String(post.totalPost ?? ""),
      recruitmentEligibility: post.eligibility || ""
    })),
    usefulLinks: (recruitment?.usefulLinks || []).map((link) => ({
      linkName: link.title || "",
      linkUrl: link.url || ""
    })),
    singleColumnDetails: []
  };
};

const resolveJobSlug = (slug, jobs, collections) => {
  if (jobs.some((job) => job.slug === slug)) {
    return slug;
  }

  for (const items of collections) {
    const matched = items.find((item) => item.slug === slug || item.jobSlug === slug);
    if (matched) {
      return matched.jobSlug || matched.slug || slug;
    }
  }

  return slug;
};

// Simple in-memory cache for selection job data by slug
const selectionJobCache = {};

function SelectionJobPage() {
  const { slug } = useParams();
  const { threeTable, headTable,fetchThreeTable,fetchHeadTable, loading: globalLoading } = useGlobalData();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");


  // Fetch context data if empty
  useEffect(() => {
    if (!threeTable?.jobs?.length && !threeTable?.results?.length) {
      fetchThreeTable();
    }
    if (!headTable?.length) {
      fetchHeadTable();
    }
  }, [threeTable, headTable, fetchThreeTable, fetchHeadTable]);

  useEffect(() => {
    let isMounted = true;
    const loadPage = async () => {
      // Check cache first
      if (selectionJobCache[slug]) {
        setPageData(selectionJobCache[slug]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      try {
        try {
          const selectedRes = await getJobSelectionBySlug(slug);
          if (isMounted) {
            setPageData(selectedRes.data);
            selectionJobCache[slug] = selectedRes.data;
          }
        } catch (selectionError) {
          let fallbackError = selectionError;
          // Use context data instead of API calls
          const jobs = threeTable.jobs || [];
          const results = threeTable.results || [];
          const admitCards = threeTable.admitCards || [];
          const answerKeys = threeTable.answerKeys || [];
          const syllabuses = threeTable.syllabuses || [];
          const admissions = threeTable.admissions || [];
          const headsRes = await getHFHeadItems();
          const recruitmentsRes = await getMFRecruitments();
          const heads = headsRes.data || [];
          const recruitments = recruitmentsRes.data || [];

          const resolvedJobSlug = resolveJobSlug(slug, jobs, [
            headTable,
            results,
            admitCards,
            answerKeys,
            syllabuses,
            admissions,
            heads,
            recruitments
          ]);

          if (resolvedJobSlug !== slug) {
            try {
              const selectedRes = await getJobSelectionBySlug(resolvedJobSlug);
              if (isMounted) {
                setPageData(selectedRes.data);
                selectionJobCache[slug] = selectedRes.data;
              }
              return;
            } catch (resolvedSelectionError) {
              fallbackError = resolvedSelectionError;
            }
          }

          const legacyMapped = mapLegacyDataToPage(
            resolvedJobSlug,
            jobs,
            admissions,
            heads,
            recruitments
          );

          if (!legacyMapped) {
            throw fallbackError;
          }

          if (isMounted) {
            setPageData(legacyMapped);
            selectionJobCache[slug] = legacyMapped;
          }
        }
      } catch (loadError) {
        if (isMounted) {
          setError(loadError.response?.data?.error || loadError.message || "Unable to load selection job details");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadPage();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, threeTable, headTable]);

  if (loading || globalLoading) {
    return <div className="selection-page-status">Loading selection details...</div>;
  }

  if (error) {
    return (
      <div className="selection-page-status error-state">
        {error}
        <div className="selection-page-actions">
          <Link to="/" className="selection-back-link">Back</Link>
        </div>
      </div>
    );
  }

  if (!pageData) {
    return null;
  }

  const displayPosts = pageData.posts?.length ? pageData.posts : [];

  const displaySingleColumnDetails = (pageData.singleColumnDetails || [])
    .map((item) => ({
      title: item?.title || "Additional Detail",
      content: item?.content || ""
    }))
    .filter((item) => item.title || item.content);

  return (
    <div className="selection-page-shell">
      <div className="selection-page-hero">
        <div>
          <h1>{pageData.jobTitle}</h1>
        </div>
        <button
          type="button"
          className="selection-back-link"
          onClick={() => window.history.back()}
        >
          Back
        </button>
      </div>

      {pageData.tableData?.length > 0 && (
        <div className="selection-table-section">
          <table className="selection-data-table">
            <tbody>
              {pageData.tableData.map((item, index) => (
                <tr key={`${item.columnTitle}-${index}`}>
                  <td className="selection-table-title">{item.columnTitle}</td>
                  <td className="selection-table-content">{renderList(item.columnItems)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {displayPosts.length > 0 && (
        <div className="selection-posts-section">
          {displayPosts.map((post, index) => (
            <div key={`${post.title}-${index}`} className="selection-post-card">
              <h3>{post.title || "Post Details"}</h3>
              {post.description && <p>{post.description}</p>}
              {post.code && <p>{post.code}</p>}
              {post.website && <h2>{post.website}</h2>}
            </div>
          ))}
        </div>
      )}

      {pageData.tableData2?.length > 0 && (
        <div className="selection-table-section">
          <table className="selection-data-table">
            <tbody>
              {pageData.tableData2.map((item, index) => (
                <tr key={`${item.columnTitle}-${index}`}>
                  <td className="selection-table-title">{item.columnTitle}</td>
                  <td className="selection-table-content">{renderList(item.columnItems)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pageData.vacancyDetails?.length > 0 && (
        <div className="selection-section-card">
          <h3>Vacancy Details</h3>
          <table className="selection-table-grid">
            <thead>
              <tr>
                <th>Post</th>
                <th>Total Post</th>
                <th>Recruitment Eligibility</th>
              </tr>
            </thead>
            <tbody>
              {pageData.vacancyDetails.map((item, index) => (
                <tr key={`${item.post}-${index}`}>
                  <td>{item.post}</td>
                  <td>{item.totalPost}</td>
                  <td>{item.recruitmentEligibility}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pageData.usefulLinks?.length > 0 && (
        <div className="selection-section-card">
          <h3>Some Useful Important Links</h3>
          <table className="selection-table-grid">
            <tbody>
              {pageData.usefulLinks.map((item, index) => (
                <tr key={`${item.linkName}-${index}`}>
                  <td>{item.linkName}</td>
                  <td>
                    <a href={item.linkUrl} target="_blank" rel="noreferrer">
                      click here
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {displaySingleColumnDetails.length > 0 && (
        <div className="selection-section-card2">
          {displaySingleColumnDetails.map((item, index) => (
            <table key={`${item.title}-${index}`} className="selection-data-table selection-single-column-table">
              <tbody>
                <tr>
                  <td className="selection-single-column-title">{item.title}</td>
                </tr>
                <tr>
                  <td className="selection-single-column-content">{item.content}</td>
                </tr>
              </tbody>
            </table>
          ))}
        </div>
      )}
    </div>
  );
}

export default SelectionJobPage;
